import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // default export
import axios from "axios";

/* ─ helper ─ */
const getUserId = () => {
  const token = localStorage.getItem("token");
  try {
    const d = jwtDecode(token);
    return d?.user?.userId || d?.userId || d?._id;
  } catch {
    return null;
  }
};

const OrderStatus = () => {
  const location = useLocation();
  const urlStatus = new URLSearchParams(location.search).get("status") || "All";

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(urlStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  const [cancelRemark, setCancelRemark] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [pendingCancelOrderId, setPendingCancelOrderId] = useState(null);

  /* ─ fetch once ─ */
  useEffect(() => {
    (async () => {
      const userId = getUserId();
      if (!userId) {
        setError("No valid user token");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `https://api.vyapakprint.com/api/businessOrders/getBusinessOrdersByUserId/${userId}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setOrders(
          Array.isArray(data?.BusinessOrders) ? data.BusinessOrders : []
        );
      } catch (err) {
        console.error(err);
        setError("Unable to load orders.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ─ cancel order function ─ */
  const changeStatus = async (orderId, newStatus, remark = "") => {
    setCancellingOrder(orderId);
    try {
      if (newStatus === "Cancelled") {
        // dedicated endpoint performs refund + status change
        await axios.patch(
          `https://api.vyapakprint.com/api/businessOrders/${orderId}/cancel`,
          { remark }
        );
      } else {
        // normal in-progress statuses
        await axios.put(
          `https://api.vyapakprint.com/api/businessOrders/updateBusinessOrderStatus/${orderId}`,
          { status: newStatus }
        );
      }

      // refresh local state so UI reflects the latest data
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );

      alert(
        newStatus === "Cancelled"
          ? "Order cancelled and amount refunded!"
          : "Order status updated successfully!"
      );
    } catch (e) {
      console.error(e);
      alert("Failed to update order status. Please try again.");
    } finally {
      setCancellingOrder(null);
    }
  };

  /* ─ format date ─ */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
  };

  const getLastStatusUpdate = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].changedAt;
  };

  const getRemarksUpdate = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].remark;
  };

  const getLastStatusImage = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].image;
  };

  /* derived lists */
  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(orders.map((o) => o.status))).sort()],
    [orders]
  );

  const visible = useMemo(
    () =>
      filter === "All" ? orders : orders.filter((o) => o.status === filter),
    [orders, filter]
  );

  /* ─ render ─ */
  return (
    <div className="container mx-auto p-6 min-h-screen">
      <h1 className="text-3xl font-bold mb-6">Order Status</h1>
      {showRemarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Cancel Order</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please provide a reason for cancellation:
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-2 mb-4"
              rows={4}
              value={cancelRemark}
              onChange={(e) => setCancelRemark(e.target.value)}
            />
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
                onClick={() => {
                  setShowRemarkModal(false);
                  setCancelRemark("");
                  setPendingCancelOrderId(null);
                }}
              >
                Cancel
              </button>
              <button
                disabled={!cancelRemark.trim()}
                className={`px-4 py-2 rounded ${
                  !cancelRemark.trim()
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-500 text-white hover:bg-red-600"
                }`}
                onClick={() => {
                  changeStatus(pendingCancelOrderId, "Cancelled", cancelRemark);
                  setShowRemarkModal(false);
                  setCancelRemark("");
                  setPendingCancelOrderId(null);
                }}
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* filter */}
      <div className="mb-6 bg-white p-4 shadow rounded-lg border">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Status
        </label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* table */}
      <div className="bg-white shadow rounded-lg overflow-auto border">
        {loading ? (
          <p className="p-6 text-center">Loading…</p>
        ) : error ? (
          <p className="p-6 text-red-600 text-center">{error}</p>
        ) : visible.length ? (
          <table className="min-w-[1300px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 text-left">#</th>
                <th className="py-3 px-4 text-left">Order ID</th>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Remarks</th>
                <th className="py-3 px-4 text-left">Firm Name</th>
                <th className="py-3 px-4 text-left">Contact Person</th>
                <th className="py-3 px-4 text-center">Quantity</th>
                <th className="py-3 px-4 text-center">Total (₹)</th>
                <th className="py-3 px-4 text-center">Last Updated</th>
                <th className="py-3 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((order, i) => {
                const lastUpdate = getLastStatusUpdate(order.statusHistory);
                const remarks = getRemarksUpdate(order.statusHistory);
                const statusImage = getLastStatusImage(order.statusHistory);
                return (
                  <tr key={order._id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">{i + 1}</td>
                    <td className="py-3 px-4 font-mono text-xs">
                      {order._id || "N/A"}
                    </td>
                    <td className="py-3 px-4">{formatDate(order.orderDate)}</td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "Cancelled"
                            ? "bg-red-100 text-red-800"
                            : order.status === "Dispatched"
                            ? "bg-green-100 text-green-800"
                            : order.status === "Printing"
                            ? "bg-blue-100 text-blue-800"
                            : order.status === "Packing"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs">
                      <div className="flex items-center justify-center gap-2">
                        <span>{remarks || "N/A"}</span>
                        {statusImage && (
                          <button
                            onClick={() => setModalImage(statusImage)}
                            className="text-blue-500 hover:text-blue-700 text-xs underline"
                          >
                            View Image
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{order.firmName || "N/A"}</td>
                    <td className="py-3 px-4">
                      {order.userDetails?.contactPerson || "N/A"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {order.quantity || 0}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold">
                      ₹{order.totalPrice || 0}
                    </td>
                    <td className="py-3 px-4 text-center text-xs">
                      {lastUpdate ? formatDateTime(lastUpdate) : "N/A"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {order.status === "Pending" ? (
                        <button
                          onClick={() => {
                            setPendingCancelOrderId(order._id);
                            setShowRemarkModal(true);
                          }}
                          disabled={cancellingOrder === order._id}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            cancellingOrder === order._id
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-red-500 text-white hover:bg-red-600"
                          }`}
                        >
                          {cancellingOrder === order._id
                            ? "Cancelling..."
                            : "Cancel Order"}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="p-6 text-gray-600 text-center">
            No orders found for the selected status.
          </p>
        )}
      </div>

      {/* summary */}
      {visible.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {visible.length} of {orders.length} orders
        </div>
      )}

      {/* Image Modal */}
      {modalImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Status Image</h3>
              <button
                onClick={() => setModalImage(null)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>
            <img
              src={modalImage}
              alt="Status update"
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderStatus;
